import {
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  EmptyState,
} from '@dev-spendesk/grapes';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type PeriodInterval } from 'src/core/modules/budgets/models/periodInterval';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { BreakdownSpendingsSection } from './BreakdownSpendingsSection';
import { BreakdownSpendingsSectionV2 } from './BreakdownSpendingsSectionV2';
import { type NodePath } from '../../../../budgets-by-dimensions/models/nodePath';
import {
  type BudgetBreakdown as Breakdown,
  getCommittedAmount,
  getUsedAmount,
} from '../../../../models/breakdown';
import { spendingTypes } from '../../../../models/breakdownSpending';
import { type ExpenseCategoryBreakdown } from '../../../../models/expenseCategoryBreakdown';
import { type PeriodRangePayload } from '../../../../models/periodRange';

type BreakdownParametersV1 = {
  type: 'v1';
  periodRange: PeriodRangePayload;
  expenseCategory?: ExpenseCategoryBreakdown['expenseCategory'];
};

type BreakdownParametersV2 = {
  type: 'v2';
  period: PeriodInterval;
  nodePath: NodePath;
};

type BreakdownParameters = BreakdownParametersV1 | BreakdownParametersV2;

export const BudgetBreakdown = ({
  budgetId,
  breakdownComputedAt,
  breakdown,
  breakdownParameters,
}: {
  budgetId: string;
  breakdownComputedAt: Date;
  breakdown: Breakdown;
  breakdownParameters: BreakdownParameters;
}) => {
  const committedAmount = getCommittedAmount(breakdown);
  const usedAmount = getUsedAmount(breakdown);

  return (
    <div>
      <Tabs>
        <TabList className="mt-12">
          <Tab>Used • {formatMonetaryValue(usedAmount)}</Tab>
          <Tab>Committed • {formatMonetaryValue(committedAmount)}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <TabContent
              budgetId={budgetId}
              breakdownPart="used"
              breakdownComputedAt={breakdownComputedAt}
              breakdownParameters={breakdownParameters}
              hasBreakdownSpendings={usedAmount.amount !== 0}
            />
          </TabPanel>
          <TabPanel>
            <TabContent
              budgetId={budgetId}
              breakdownPart="committed"
              breakdownComputedAt={breakdownComputedAt}
              breakdownParameters={breakdownParameters}
              hasBreakdownSpendings={committedAmount.amount !== 0}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

const TabContent = ({
  budgetId,
  breakdownComputedAt,
  breakdownParameters,
  breakdownPart,
  hasBreakdownSpendings,
}: {
  budgetId: string;
  breakdownComputedAt: Date;
  breakdownParameters: BreakdownParameters;
  breakdownPart: 'used' | 'committed';
  hasBreakdownSpendings: boolean;
}) => {
  const { t } = useTranslation('global');
  if (!hasBreakdownSpendings) {
    return (
      <EmptyState
        iconName="circle-information"
        iconVariant="blue"
        className="mt-40"
        title={t('budget.breakdownSpendings.noBreakdownSpendings.title')}
        subtitle={t('budget.breakdownSpendings.noBreakdownSpendings.subtitle')}
      />
    );
  }
  return spendingTypes.map((spendingType) => {
    if (
      // FIXME: this case shouldn't not happen, to fix in the backend
      spendingType === 'physicalCard' &&
      breakdownPart === 'committed'
    ) {
      return null;
    }
    if (breakdownParameters.type === 'v2') {
      return (
        <BreakdownSpendingsSectionV2
          key={spendingType}
          spendingType={spendingType}
          breakdownPart={breakdownPart}
          budgetId={budgetId}
          period={breakdownParameters.period}
          nodePath={breakdownParameters.nodePath}
        />
      );
    }
    return (
      <BreakdownSpendingsSection
        key={spendingType}
        spendingType={spendingType}
        breakdownPart={breakdownPart}
        budgetId={budgetId}
        periodRange={breakdownParameters.periodRange}
        breakdownComputedAt={breakdownComputedAt}
        expenseCategory={breakdownParameters.expenseCategory}
      />
    );
  });
};
